import { isEmpty, pathOr } from 'ramda'
import { RSAA } from 'redux-api-middleware'
import {
  getApiPath,
  makeApiTypes,
  makeQueryParams,
  parsePaymentMethodsFromSource,
  webStorage,
} from 'sidecar-utility-library'
import { datadogLogs } from '@datadog/browser-logs'

export const types = {
  ...makeApiTypes('ADD_CARD', 'paymentMethod'),
  ...makeApiTypes('ADD_ACH', 'paymentMethod'),
  ...makeApiTypes('UPDATE_ACH', 'paymentMethod'),
  ...makeApiTypes('FETCH_PAYMENT_METHODS', 'paymentMethod'),
  ...makeApiTypes('SET_PRIMARY_METHOD', 'paymentMethod'),
  ...makeApiTypes('REMOVE_METHOD', 'paymentMethod'),
  ...makeApiTypes('UPDATE_PLAID_LINK_TOKEN', 'paymentMethod'),
  ...makeApiTypes('CREATE_ACH_SETUP_INTENT', 'paymentMethod'),
  ...makeApiTypes('COMPLETE_ACH_SETUP_INTENT', 'paymentMethod'),
  CLEAR_ACH_SETUP_INTENT: 'paymentMethod/CLEAR_ACH_SETUP_INTENT',
  CATCH_CARD_ERROR: 'paymentMethod/CATCH_CARD_ERROR',
  CLEAR_CARD_ERRORS: 'paymentMethod/CLEAR_CARD_ERRORS',
  SAVE_STRIPE_FORM: 'paymentMethod/SAVE_STRIPE_FORM',
  SET_PAYMENT_METHODS_BUSY: 'paymentMethod/SET_PAYMENT_METHODS_BUSY',
  REMOVE_STRIPE_FORM: 'paymentMethod/REMOVE_STRIPE_FORM',
  ...makeApiTypes('ADD_MULTIPLE_METHODS', 'paymentMethod'),
  SAVE_ACH_ITEM: 'paymentMethod/SAVE_ACH_ITEM',
  REMOVE_ACH_ITEM: 'paymentMethod/REMOVE_ACH_ITEM',
}

export const addCard = (card, insuranceProduct = '') => {
  let payload = { ...card }
  if (insuranceProduct === 'GFI' || insuranceProduct === 'ASO') {
    payload = {
      ...payload,
      primaryForExpense: true,
      primaryForSubscription: false,
    }
  }

  return {
    [RSAA]: {
      endpoint: getApiPath(
        'exp',
        `paymentmethods/card${makeQueryParams({
          accountUuid: webStorage.getItem('aud'),
          // uncomment to make successful call but not save
          // test: true,
        })}`,
      ),
      method: 'PUT',
      body: JSON.stringify(payload),
      types: [
        types.ADD_CARD_REQUEST,
        types.ADD_CARD_SUCCESS,
        types.ADD_CARD_ERROR,
      ],
    },
  }
}

export const addAch = (achInfo, insuranceProduct = '') => {
  let payload = { ...achInfo }
  if (insuranceProduct === 'GFI' || insuranceProduct === 'ASO') {
    payload = {
      ...payload,
      primaryForExpense: true,
      primaryForSubscription: false,
    }
  }

  return {
    [RSAA]: {
      endpoint: getApiPath(
        'exp',
        `paymentmethods/bank-account${makeQueryParams({
          accountUuid: webStorage.getItem('aud'),
          // uncomment to make successful call but not save
          // test: true,
        })}`,
      ),
      method: 'PUT',
      body: JSON.stringify(payload),
      types: [
        types.ADD_ACH_REQUEST,
        types.ADD_ACH_SUCCESS,
        types.ADD_ACH_ERROR,
      ],
    },
  }
}

export const updateAch = (achInfo, methodId) => ({
  [RSAA]: {
    endpoint: getApiPath(
      'exp',
      `paymentmethods/bank-account${makeQueryParams({
        accountUuid: webStorage.getItem('aud'),
        remove: true,
        id: methodId,
      })}`,
    ),
    method: 'PUT',
    body: JSON.stringify(achInfo),
    types: [
      types.UPDATE_ACH_REQUEST,
      types.UPDATE_ACH_SUCCESS,
      types.UPDATE_ACH_ERROR,
    ],
  },
})

export const updatePlaidLinkToken = userId => ({
  [RSAA]: {
    endpoint: getApiPath('exp', `link_token/pub/create`),
    headers: {
      'x-api-key': process.env.REACT_APP_X_API_KEY,
    },
    method: 'POST',
    body: JSON.stringify({
      countryCode: 'US',
      language: 'en',
      userID: webStorage.getItem('aud') || userId,
    }),
    types: [
      types.UPDATE_PLAID_LINK_TOKEN_REQUEST,
      types.UPDATE_PLAID_LINK_TOKEN_SUCCESS,
      types.UPDATE_PLAID_LINK_TOKEN_ERROR,
    ],
  },
})

/**
 * Creates a Stripe SetupIntent for linking a bank account (ACH) via Stripe
 * Financial Connections. The `clientSecret` in the response is what the UI
 * needs to mount `<Elements>`, and the `setupIntentId` is what
 * `completeAchSetupIntent` needs afterwards.
 *
 * @param {string} [nonce] - Fresh single-use idempotency nonce forwarded to
 *   Stripe as the SetupIntent's `Idempotency-Key`. Mint one per logical attempt
 *   (see `useIdempotencyNonce`) so a retried request returns the original
 *   SetupIntent instead of minting a second one; a member with two `pm_*` for
 *   the same bank account is what this prevents. Omitting it is allowed and
 *   matches the pre-TMT-3492 behavior.
 * @returns {Object} RSAA action object. Success payload is `{ setupIntentId,
 *   clientSecret }`
 */
export const createAchSetupIntent = nonce => ({
  [RSAA]: {
    endpoint: getApiPath('exp', 'ach-bank-account/create'),
    method: 'POST',
    body: JSON.stringify({ accountUuid: webStorage.getItem('aud'), nonce }),
    types: [
      types.CREATE_ACH_SETUP_INTENT_REQUEST,
      types.CREATE_ACH_SETUP_INTENT_SUCCESS,
      types.CREATE_ACH_SETUP_INTENT_ERROR,
    ],
  },
})

/**
 * Completes a Stripe SetupIntent after the member has linked their bank
 * account, so the backend can retrieve and persist the resulting PaymentMethod.
 * Note the front end never sends the `pm_*` — it only carries the
 * `setupIntentId` through from `createAchSetupIntent`.
 *
 * IMPORTANT: if this call fails, the correct recovery is to retry THIS call
 * with the same `setupIntentId` — never restart the Stripe linking flow. This
 * endpoint is idempotent: it re-retrieves the already-succeeded SetupIntent by
 * id and dedups on `paymentGatewayId`. Restarting the linking flow mints a
 * SECOND distinct `pm_*` for the same bank account, because Stripe does not
 * deduplicate PaymentMethods by underlying bank account, which leaves the
 * member with duplicate payment methods.
 *
 * @param {string} setupIntentId - The `seti_...` id returned by
 *   `createAchSetupIntent`
 * @returns {Object} RSAA action object. Success payload is `{ status,
 *   paymentMethodId, bankName, last4 }` when the SetupIntent succeeded, or `{
 *   status, lastSetupError }` for any other status
 */
export const completeAchSetupIntent = setupIntentId => ({
  [RSAA]: {
    endpoint: getApiPath('exp', `ach-bank-account/${setupIntentId}/complete`),
    method: 'POST',
    body: JSON.stringify({ accountUuid: webStorage.getItem('aud') }),
    types: [
      types.COMPLETE_ACH_SETUP_INTENT_REQUEST,
      types.COMPLETE_ACH_SETUP_INTENT_SUCCESS,
      types.COMPLETE_ACH_SETUP_INTENT_ERROR,
    ],
  },
})

/**
 * Forgets the current SetupIntent. Dispatch this when the member leaves the add
 * bank account form, because a SetupIntent can only be confirmed once — keeping
 * a consumed one around would mount Stripe's `<Elements>` with a dead
 * `clientSecret` the next time the member opens the form.
 *
 * @returns {Object} Plain action object
 */
export const clearAchSetupIntent = () => ({
  type: types.CLEAR_ACH_SETUP_INTENT,
})

/**
 * Fetches payment methods for the logged-in member's account
 *
 * @param {Object} params - Parameters object
 * @param {string} [params.memberUuid=''] - Optional member UUID for filtering
 *   payment methods. Default is `''`
 * @param {boolean} [params.useAccountCredit=false] - Whether to include account
 *   credit as a payment method. Default is `false`
 * @param {string} [params.expenseUuid=''] - Optional expense UUID for
 *   context-specific payment methods. Default is `''`
 * @returns {Object | undefined} RSAA action object, or undefined if accountUuid
 *   is not in webStorage
 */
export const fetchPaymentMethods = ({
  memberUuid = '',
  useAccountCredit = false,
  expenseUuid = '',
} = {}) => {
  const accountUuid = webStorage.getItem('aud') || null
  if (!accountUuid) {
    datadogLogs.logger.warn(
      'No accountUuid in webStorage for member fetchPaymentMethods',
    )
    return {
      type: 'account/FETCH_PAYMENT_METHODS_ERROR',
    }
  }

  return {
    [RSAA]: {
      endpoint: getApiPath(
        'exp',
        `paymentmethods${makeQueryParams({
          accountUuid,
          memberUuid,
          useAccountCredit,
          expenseUuid,
        })}`,
      ),
      method: 'GET',
      types: [
        types.FETCH_PAYMENT_METHODS_REQUEST,
        types.FETCH_PAYMENT_METHODS_SUCCESS,
        types.FETCH_PAYMENT_METHODS_ERROR,
      ],
    },
  }
}

// TODO: rename to updatePrimaryPayMethod
export const setPrimaryPayMethod = method => ({
  [RSAA]: {
    endpoint: getApiPath(
      `exp`,
      `paymentmethods/primary${makeQueryParams({
        accountUuid: webStorage.getItem('aud'),
        // uncomment to make successful call but not save
        // test: true,
      })}`,
    ),
    method: 'PUT',
    body: JSON.stringify(method),
    types: [
      {
        type: types.SET_PRIMARY_METHOD_REQUEST,
        meta: { method },
      },
      types.SET_PRIMARY_METHOD_SUCCESS,
      types.SET_PRIMARY_METHOD_ERROR,
    ],
  },
})

export const removePaymentMethod = method => ({
  [RSAA]: {
    endpoint: getApiPath(
      `exp`,
      `paymentmethods${makeQueryParams({
        accountUuid: webStorage.getItem('aud'),
      })}`,
    ),
    method: 'DELETE',
    body: JSON.stringify(method),
    types: [
      types.REMOVE_METHOD_REQUEST,
      types.REMOVE_METHOD_SUCCESS,
      types.REMOVE_METHOD_ERROR,
    ],
  },
})

export const catchCardError = response => ({
  type: types.CATCH_CARD_ERROR,
  payload: response || 'Something went wrong',
})

export const clearCardError = () => ({
  type: types.CLEAR_CARD_ERRORS,
})

export const saveStripeForm = (primaryFor, stripeForm) => ({
  type: types.SAVE_STRIPE_FORM,
  payload: { primaryFor, stripeForm },
})

export const removeStripeForm = primaryFor => ({
  type: types.REMOVE_STRIPE_FORM,
  payload: { primaryFor },
})

export const initialState = {
  paymentMethods: {
    busy: false,
    error: '',
    entities: [],
  },
  addCard: {
    busy: false,
    error: '',
  },
  stripeForms: {},
  achItems: {},
  plaidLinkToken: {
    busy: false,
    error: '',
    token: '',
  },
  achSetupIntent: {
    busy: false,
    error: null,
    clientSecret: null,
    setupIntentId: null,
  },
  ignoreSwipeDetails: {
    busy: false,
    error: '',
    entities: [],
  },
}

export default (state = initialState, { type, payload = {} } = {}) => {
  switch (type) {
    case types.ADD_ACH_REQUEST:
    case types.UPDATE_ACH_REQUEST:
    case types.FETCH_PAYMENT_METHODS_REQUEST:
    case types.SET_PRIMARY_METHOD_REQUEST:
    case types.REMOVE_METHOD_REQUEST:
    case types.ADD_MULTIPLE_METHODS_REQUEST: {
      return {
        ...state,
        paymentMethods: {
          ...state.paymentMethods,
          busy: true,
        },
      }
    }

    case types.UPDATE_PLAID_LINK_TOKEN_REQUEST: {
      return {
        ...state,
        plaidLinkToken: {
          ...state.plaidLinkToken,
          busy: true,
        },
      }
    }

    case types.CREATE_ACH_SETUP_INTENT_REQUEST:
    case types.COMPLETE_ACH_SETUP_INTENT_REQUEST: {
      return {
        ...state,
        achSetupIntent: {
          ...state.achSetupIntent,
          busy: true,
          error: null,
        },
      }
    }

    case types.ADD_ACH_SUCCESS:
    case types.UPDATE_ACH_SUCCESS:
    case types.FETCH_PAYMENT_METHODS_SUCCESS:
    case types.SET_PRIMARY_METHOD_SUCCESS:
    case types.REMOVE_METHOD_SUCCESS:
    case types.ADD_MULTIPLE_METHODS_SUCCESS: {
      return {
        ...state,
        paymentMethods: {
          ...state.paymentMethods,
          busy: false,
          entities: parsePaymentMethodsFromSource(payload),
        },
      }
    }

    case types.SET_PRIMARY_METHOD_ERROR: {
      return {
        ...state,
        paymentMethods: {
          ...state.paymentMethods,
          busy: false,
          error: `An error occurred while changing your autopayment method. Please retry later, or contact Member Care.`,
        },
      }
    }

    case types.ADD_CARD_REQUEST: {
      return {
        ...state,
        addCard: {
          ...state.addCard,
          busy: true,
          error: '',
        },
      }
    }
    case types.ADD_CARD_SUCCESS: {
      return {
        ...state,
        addCard: {
          ...state.addCard,
          busy: false,
        },
        paymentMethods: {
          ...state.paymentMethods,
          busy: false,
          entities: parsePaymentMethodsFromSource(payload),
        },
      }
    }
    case types.ADD_CARD_ERROR: {
      const errorMessage = _formatErrorResponse(payload)
      return {
        ...state,
        addCard: {
          ...state.addCard,
          busy: false,
          error: errorMessage,
        },
      }
    }

    case types.UPDATE_PLAID_LINK_TOKEN_SUCCESS: {
      return {
        ...state,
        plaidLinkToken: {
          ...state.plaidLinkToken,
          busy: false,
          token: payload.linkToken,
        },
      }
    }
    case types.CREATE_ACH_SETUP_INTENT_SUCCESS: {
      return {
        ...state,
        achSetupIntent: {
          ...state.achSetupIntent,
          busy: false,
          clientSecret: payload.clientSecret,
          setupIntentId: payload.setupIntentId,
        },
      }
    }

    // The completion result (status, bank details) is returned to the caller by
    // useAchBankAccount rather than stored here — only busy/error live in redux.
    case types.COMPLETE_ACH_SETUP_INTENT_SUCCESS: {
      return {
        ...state,
        achSetupIntent: {
          ...state.achSetupIntent,
          busy: false,
        },
      }
    }

    case types.CREATE_ACH_SETUP_INTENT_ERROR:
    case types.COMPLETE_ACH_SETUP_INTENT_ERROR: {
      return {
        ...state,
        achSetupIntent: {
          ...state.achSetupIntent,
          busy: false,
          error: _formatErrorResponse(payload),
        },
      }
    }

    case types.CLEAR_ACH_SETUP_INTENT: {
      return {
        ...state,
        achSetupIntent: initialState.achSetupIntent,
      }
    }

    case types.ADD_ACH_ERROR:
    case types.UPDATE_ACH_ERROR:
    case types.FETCH_PAYMENT_METHODS_ERROR:
    case types.REMOVE_METHOD_ERROR:
    case types.ADD_MULTIPLE_METHODS_ERROR: {
      const errorMessage = _formatErrorResponse(payload)
      return {
        ...state,
        paymentMethods: {
          ...state.paymentMethods,
          busy: false,
          error: errorMessage,
        },
      }
    }

    case types.UPDATE_PLAID_LINK_TOKEN_ERROR: {
      return {
        ...state,
        plaidLinkToken: {
          ...state.plaidLinkToken,
          busy: false,
          error: payload.response.message,
        },
      }
    }

    case types.CATCH_CARD_ERROR: {
      return {
        ...state,
        paymentMethods: {
          ...state.paymentMethods,
          error: payload.error.message,
        },
      }
    }

    case types.CLEAR_CARD_ERRORS: {
      return {
        ...state,
        paymentMethods: {
          ...state.paymentMethods,
          error: '',
        },
      }
    }

    case types.SAVE_STRIPE_FORM: {
      return {
        ...state,
        stripeForms: {
          ...state.stripeForms,
          [payload.primaryFor]: { ...payload.stripeForm },
        },
      }
    }

    case types.REMOVE_STRIPE_FORM: {
      const newStripeForms = { ...state.stripeForms }
      delete newStripeForms[payload.primaryFor]
      return {
        ...state,
        stripeForms: newStripeForms,
      }
    }

    case types.SAVE_ACH_ITEM: {
      return {
        ...state,
        achItems: {
          ...state.achItems,
          [payload.primaryFor]: { ...payload.achInfo },
        },
      }
    }

    case types.REMOVE_ACH_ITEM: {
      const newAchItems = { ...state.achItems }
      delete newAchItems[payload.primaryFor]
      return {
        ...state,
        achItems: newAchItems,
      }
    }

    case types.SET_PAYMENT_METHODS_BUSY: {
      return {
        ...state,
        paymentMethods: {
          ...state.paymentMethods,
          busy: payload,
        },
      }
    }

    default:
      return state
  }
}

/** -=-=-=-=-=-=-=-=- PRIVATE METHODS -=-=-=-=-=-=-=-=- */

const _formatErrorResponse = (payload = {}) => {
  let errorMessage = pathOr('', ['response', 'message'], payload)

  if (!isEmpty(errorMessage) && payload.response?.errors) {
    errorMessage += '\n'
  }

  if (payload.response?.errors && !isEmpty(payload.response.errors)) {
    errorMessage += payload.response.errors.join('\n')
  }

  return errorMessage
}
